import { useEffect, useMemo, useState } from 'react';
import { ExternalLink, Pencil } from 'lucide-react';
import { useFundraiserStore } from '../../store/fundraiserStore';
import { EditFundraiserModal } from '../competitors/EditFundraiserModal';
import { ScreenshotThumb } from '../competitors/ScreenshotThumb';
import type { CompetitorFundraiser, CompetitorPlatform, CampaignStatus } from '../../types';

interface CompetitorFundraisersProps {
  organizationId: string;
  /** Shows an edit control on each fundraiser (amounts, status, year, sport). */
  canEdit?: boolean;
}

const PLATFORM_LABELS: Record<CompetitorPlatform, string> = {
  VERTICAL_RAISE: 'VR',
  SNAP_RAISE: 'Snap',
  ETEAM_SPONSOR: 'ETS',
  SCHOOL_FUNDR: 'SF',
};

const PLATFORM_COLORS: Record<CompetitorPlatform, string> = {
  VERTICAL_RAISE: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  SNAP_RAISE: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  ETEAM_SPONSOR: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  SCHOOL_FUNDR: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
};

const STATUS_COLORS: Record<CampaignStatus, string> = {
  ACTIVE: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  ENDED: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  UNKNOWN: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
};

function formatCurrency(amount: number | null): string {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export const CompetitorFundraisers = ({ organizationId, canEdit = false }: CompetitorFundraisersProps) => {
  const { orgFundraisers, isLoadingOrgFundraisers, fetchOrgFundraisers } = useFundraiserStore();
  const [editingFundraiser, setEditingFundraiser] = useState<CompetitorFundraiser | null>(null);

  useEffect(() => {
    fetchOrgFundraisers(organizationId);
  }, [organizationId, fetchOrgFundraisers]);

  if (isLoadingOrgFundraisers) {
    return (
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Competitor Fundraising
        </h2>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (orgFundraisers.length === 0) {
    return null;
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Competitor Fundraising
        </h2>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
          {orgFundraisers.length}
        </span>
      </div>

      <GroupedFundraiserList fundraisers={orgFundraisers} onEdit={canEdit ? setEditingFundraiser : undefined} />

      {canEdit && (
        <EditFundraiserModal
          fundraiser={editingFundraiser}
          isOpen={editingFundraiser !== null}
          onClose={() => setEditingFundraiser(null)}
          onSuccess={() => {
            setEditingFundraiser(null);
            fetchOrgFundraisers(organizationId);
          }}
          onScreenshotsChanged={() => fetchOrgFundraisers(organizationId)}
        />
      )}
    </div>
  );
};

function GroupedFundraiserList({
  fundraisers,
  onEdit,
}: {
  fundraisers: CompetitorFundraiser[];
  onEdit?: (fundraiser: CompetitorFundraiser) => void;
}) {
  const grouped = useMemo(() => {
    const bySport: Record<string, { sportName: string; fundraisers: CompetitorFundraiser[] }> = {};
    const unlinked: CompetitorFundraiser[] = [];

    for (const f of fundraisers) {
      if (f.sportId) {
        if (!bySport[f.sportId]) {
          bySport[f.sportId] = { sportName: f.sport || 'Unknown Sport', fundraisers: [] };
        }
        bySport[f.sportId].fundraisers.push(f);
      } else {
        unlinked.push(f);
      }
    }

    return { bySport, unlinked };
  }, [fundraisers]);

  const hasSportGroups = Object.keys(grouped.bySport).length > 0;

  // If no grouping needed, render flat list
  if (!hasSportGroups) {
    return (
      <div className="space-y-3">
        {fundraisers.map(f => <FundraiserRow key={f.id} fundraiser={f} onEdit={onEdit} />)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {Object.entries(grouped.bySport).map(([sportId, { sportName, fundraisers: sportFundraisers }]) => (
        <div key={sportId}>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{sportName}</h3>
          <div className="space-y-2">
            {sportFundraisers.map(f => <FundraiserRow key={f.id} fundraiser={f} onEdit={onEdit} />)}
          </div>
        </div>
      ))}
      {grouped.unlinked.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Other / Unlinked</h3>
          <div className="space-y-2">
            {grouped.unlinked.map(f => <FundraiserRow key={f.id} fundraiser={f} onEdit={onEdit} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function FundraiserRow({
  fundraiser,
  onEdit,
}: {
  fundraiser: CompetitorFundraiser;
  onEdit?: (fundraiser: CompetitorFundraiser) => void;
}) {
  const label = fundraiser.campaignName || [fundraiser.schoolName, fundraiser.sport].filter(Boolean).join(' - ');

  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
      <div className="flex items-center space-x-3 min-w-0 flex-1">
        {/* Platform badge */}
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${PLATFORM_COLORS[fundraiser.platform]}`}>
          {PLATFORM_LABELS[fundraiser.platform]}
        </span>

        {/* Fundraiser info */}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            {label}
          </div>
          <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
            {fundraiser.year && <span>{fundraiser.year}</span>}
            {fundraiser.status && (
              <span className={`inline-flex items-center px-1.5 py-0 rounded text-xs ${STATUS_COLORS[fundraiser.status]}`}>
                {fundraiser.status.toLowerCase()}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-4 ml-4">
        <ScreenshotThumb fundraiser={fundraiser} />
        {/* Raised amount */}
        <div className="text-right">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {formatCurrency(fundraiser.raisedAmount)}
          </div>
          {fundraiser.blastAmount !== null && fundraiser.blastAmount !== undefined && (
            <div className="text-xs text-blast-600 dark:text-blast-400">
              Blast: {formatCurrency(fundraiser.blastAmount)}
            </div>
          )}
        </div>

        {/* External link */}
        {fundraiser.campaignUrl && (
          <a
            href={fundraiser.campaignUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
        {onEdit && (
          <button
            type="button"
            onClick={() => onEdit(fundraiser)}
            aria-label="Edit fundraiser"
            title="Edit fundraiser"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
